import React, { FC } from 'react'
import './ResourceCard.less'
import classNames from 'classnames'
import { clone } from 'lodash'
import { Trans, Plural, t } from '@lingui/macro'
import { useDisclosure } from '@chakra-ui/react'
import {
  Box,
  Flex,
  Tooltip,
  Modal,
  ButtonPrimary
} from 'front-end/src/components'

import {
  PathwayEnrolmentStatusEnum,
  PathwayModelAsStudent,
  PathwayProgress,
  PathwayVersionModelAsStudent,
  UserModelPublicBase,
  WorkbookStatusEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import { Card, ICardProps } from '../../../components/common/card/Card'
import CardTitle from '../../../components/common/card/CardTitle'
import CardBody from '../../../components/common/card/CardBody'
import { imageVariants2srcSetDensity } from '../../../components/utils/imageVariants2srcSetDensity'
import { linkBuilder } from '../../../../../shared/linkBuilder'
import { navigateTo } from '../../../app/util/appHistory'
import { CardAssignedPin } from '../../../components/common/card/CardAssignedPin'
import { fontWeights } from 'front-end/src/components/chakraThemeExtension'
import { dayjsFromNow } from 'shared/dayjs'
import { CardStatuses } from '../../../components/common/card/CardStatuses'
import {
  StatusProgressCompleted,
  StatusProgressCompletedInverted,
  StatusProgressDue,
  StatusProgressFailed,
  StatusProgressNew,
  StatusProgressUpdated
} from '../../../components/common/status/Status'
import { isTopicCompletedOutsidePathway } from '../../pathway-detail-page/helpers'

export interface ITopicEnrolment {
  id?: number
  target_completion_date?: string | null
  target_date?: string | null // pathway enrolments have it like this. TODO unify this with topics in the DB
  completion_date?: string | Date | null
  share_batch_ts?: string | null
  deleted_at?: Date | null
  progress?: number | null | undefined
  status?: WorkbookStatusEnum | null
  canBeUpdated?: boolean
}

export type CardStatusesType = JSX.Element[]

export interface IResourceCardProps {
  title: string | null | undefined
  description: string | null | undefined
  image?: string | null
  disabled?: boolean
  failedPathway?: boolean
  firstTimeDisabled?: boolean
  imageSrcSet?: string | null
  author: Partial<UserModelPublicBase>
  readDurationMins?: number | null
  enrolment?: ITopicEnrolment
  topicId: number
  pathway?: Partial<
    PathwayModelAsStudent & { latestVersion: PathwayVersionModelAsStudent[] }
  >
  className?: string
  ctaButton?: ICardProps['ctaButton']
}

export const ResourceCard: FC<IResourceCardProps> = (props) => {
  const {
    title,
    description,
    image,
    imageSrcSet,
    author,
    readDurationMins,
    enrolment,
    topicId,
    pathway,
    disabled,
    failedPathway,
    firstTimeDisabled,
    className,
    children,
    ctaButton
  } = props
  const { isOpen, onOpen, onClose } = useDisclosure()
  const classes = classNames('ResourceCard', className)
  const targetDate =
    enrolment && (enrolment.target_completion_date || enrolment.target_date)
  const isCompleted = enrolment?.status === WorkbookStatusEnum.COMPLETED

  const shouldRenderPin = !!(enrolment?.share_batch_ts && !isCompleted)

  const pathwayCompleted =
    pathway?.currentEnrolment?.status === PathwayEnrolmentStatusEnum.COMPLETED

  const isCompletedArchivedEnrolment =
    pathwayCompleted && isCompleted && enrolment?.deleted_at // we only want to show archived enrolment green tick when the whole pathway is done

  const linkUrl = pathway?.id
    ? linkBuilder.topicUrlViaPathway(pathway.id, topicId)
    : linkBuilder.topicUrl(topicId)

  let statuses: CardStatusesType = []

  if (targetDate && !isCompleted && enrolment?.status) {
    statuses.push(
      <StatusProgressDue dueDate={targetDate} status={enrolment?.status} />
    )
  }

  if (pathway?.currentEnrolment?.newTopicsInPathway?.includes(topicId)) {
    statuses.push(<StatusProgressNew />)
  }

  if (enrolment?.canBeUpdated) {
    statuses.push(<StatusProgressUpdated />)
  }

  if (enrolment?.status === WorkbookStatusEnum.COMPLETED) {
    statuses.push(<StatusProgressCompleted />)
  }

  const latestVersion = pathway?.latestVersion && pathway?.latestVersion[0]
  const lastEnrolment = latestVersion?.topics?.find(
    (topic) => topic.id === topicId
  )?.lastEnrolment

  const isCompletedOutside = isTopicCompletedOutsidePathway(
    enrolment?.status,
    lastEnrolment?.status,
    lastEnrolment?.completion_date,
    pathway?.currentEnrolment?.completed_at
  )

  if (isCompletedOutside) {
    statuses.push(
      <Tooltip
        text={t`This resource was completed after this pathway was completed`}
      >
        <StatusProgressCompletedInverted />
      </Tooltip>
    )
  }

  if (enrolment?.status === WorkbookStatusEnum.FAILED) {
    statuses.push(<StatusProgressFailed />)
  }

  return (
    <Box>
      <Card
        ctaButton={ctaButton}
        disabled={
          (disabled || !!isCompletedArchivedEnrolment) && !isCompletedOutside
        }
        className={classes}
        image={image}
        linkUrl={linkUrl}
        imageSrcSet={imageSrcSet}
        isLocked={
          pathway?.progress !== PathwayProgress.open &&
          disabled &&
          !isCompletedOutside &&
          !enrolment?.completion_date
        }
        isFailed={
          failedPathway &&
          firstTimeDisabled &&
          pathway?.progress === PathwayProgress.locked
        }
        offsetTop={!image && (shouldRenderPin || statuses.length > 0)}
        onClick={() => {
          if (isCompletedArchivedEnrolment) {
            onOpen()
          }
        }}
      >
        <Flex maxHeight="154px" flexDirection="column">
          <CardStatuses statuses={statuses} offsetRight={shouldRenderPin} />
          {children}
          <CardTitle title={title} />
          {description && <CardBody>{description}</CardBody>}
          <ResourceCardFooter
            author={author}
            readDurationMins={readDurationMins}
          />
          {shouldRenderPin && enrolment?.status && (
            <CardAssignedPin status={enrolment.status} />
          )}
        </Flex>
      </Card>
      {isCompletedArchivedEnrolment && (
        <Modal
          header={t`Your enrolment is archived`}
          show={isOpen}
          onHide={onClose}
          controls={[
            <ButtonPrimary
              key={1}
              onClick={() => {
                navigateTo(linkUrl)
                onClose()
              }}
            >
              <Trans>Go to current version</Trans>
            </ButtonPrimary>
          ]}
        >
          <Trans>
            You have completed this resource on
            <Box fontWeight={fontWeights.semibold} display="inline">
              {/* @ts-expect-error */}
              {dayjsFromNow(enrolment.completion_date)}
            </Box>
            , but your enrolment was archived. Your progress in resource
            <br />
            &ldquo;{title}&rdquo; might be different.
          </Trans>
        </Modal>
      )}
    </Box>
  )
}

const ResourceCardFooter = ({
  author,
  readDurationMins
}: {
  author: Partial<UserModelPublicBase>
  readDurationMins?: number | null
}) => {
  return (
    <div className="Card__footer">
      <div className="Card__details">
        <div className="Card__author">
          <Trans>by {author?.display_name}</Trans>
        </div>
        <div className="Card__read-duration">
          <Plural
            value={readDurationMins ?? 0}
            _0="< 1 min read"
            /**
              On backend, we round duration seconds UP, for example 55 seconds is rounded UP to 1 minute.
              That's the reason why we show "< 1 min read" for 1 minute duration.
            */
            one="< 1 min read"
            other="# mins read"
          />
        </div>
      </div>
    </div>
  )
}

export function resourceCardCoverImageSrcSet(coverImageVariants) {
  if (!coverImageVariants) {
    return
  }

  return imageVariants2srcSetDensity(
    coverImageVariants
      .filter((imageVariant) => {
        return ['300x200', '600x300'].includes(imageVariant.size_key)
      })
      .map((imageVariant) => {
        const withDensity = clone(imageVariant)
        switch (withDensity.size_key) {
          case '300x200':
            withDensity.density = 1
            break
          case '600x300':
            withDensity.density = 2
            break
        }
        return withDensity
      })
  )
}
